import { motion } from 'framer-motion';
import { Globe, Building2, TrendingUp, Lightbulb, Users as UsersIcon, CheckCircle2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const Services = () => {
  const services = [
    {
      icon: <Globe className="w-12 h-12" />,
      title: 'Relocation & Expatriation Services',
      description: 'Comprehensive support for individuals and families moving to the UAE',
      color: '#3498DB',
      features: [
        'Visa processing and immigration support',
        'Housing search and accommodation assistance',
        'School placement for children',
        'Bank account opening support',
        'Healthcare and insurance guidance',
        'Cultural orientation and settling-in services',
      ],
    },
    {
      icon: <Building2 className="w-12 h-12" />,
      title: 'Business Setup & Corporate Services',
      description: 'End-to-end business formation and registration services',
      color: '#27AE60',
      features: [
        'Company formation in Free Zones and Mainland',
        'Trade license application and processing',
        'Local sponsorship arrangements',
        'Office space solutions and PRO services',
        'Business plan development',
        'Bank account opening for companies',
      ],
    },
    {
      icon: <TrendingUp className="w-12 h-12" />,
      title: 'Financial, Tax & Compliance',
      description: 'Expert financial advisory and regulatory compliance support',
      color: '#5DADE2',
      features: [
        'Tax planning and optimization strategies',
        'VAT registration and compliance',
        'Corporate tax advisory services',
        'Financial reporting and audit support',
        'Transfer pricing documentation',
        'Anti-money laundering compliance',
      ],
    },
    {
      icon: <Lightbulb className="w-12 h-12" />,
      title: 'SAM Incubator',
      description: 'Support program for startups and early-stage businesses',
      color: '#D4AF37',
      features: [
        'Business mentorship and coaching',
        'Market research and feasibility studies',
        'Investor pitch deck development',
        'Networking opportunities',
        'Access to funding networks',
        'Marketing and branding strategy',
      ],
    },
    {
      icon: <UsersIcon className="w-12 h-12" />,
      title: 'SAM Talent & Solutions',
      description: 'Human Capital Advisory',
      color: '#E74C3C',
      features: [
        'Talent Strategy & Operations',
        'Talent acquisition strategies',
        'HR policy development',
        'Employment contract preparation',
        'Payroll and benefits administration',
        'Work permit processing for employees',
      ],
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.25, 0.1, 0.25, 1] } }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="services-page">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center text-white pt-20" data-testid="services-hero-section">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1920" 
            alt="Corporate"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#0a0a0a]/70 to-[#0a0a0a]" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <div className="flex items-center justify-center gap-4 mb-6">
              <div className="w-20 h-[1px] bg-gradient-to-r from-transparent to-[#3498DB]" />
              <span className="text-[#3498DB] font-semibold tracking-[0.2em] uppercase text-sm">What We Offer</span>
              <div className="w-20 h-[1px] bg-gradient-to-l from-transparent to-[#3498DB]" />
            </div>
            
            <h1 className="text-5xl md:text-7xl font-bold mb-6" data-testid="services-hero-title">
              Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3498DB] to-[#27AE60]">Services</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Comprehensive solutions for your business and relocation needs in the UAE
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 bg-[#0a0a0a]" data-testid="services-list-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
            className="space-y-12"
          >
            {services.map((service, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className={`grid grid-cols-1 lg:grid-cols-2 gap-8 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`service-detail-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-10 group hover:border-white/20 transition-all duration-500">
                    <div className="absolute top-0 right-0 w-60 h-60 rounded-full blur-3xl opacity-20" style={{ background: service.color }} />
                    
                    <motion.div 
                      className="w-20 h-20 rounded-2xl flex items-center justify-center mb-8"
                      style={{ background: `${service.color}15`, color: service.color }}
                      whileHover={{ scale: 1.1, rotate: 5 }}
                    >
                      {service.icon}
                    </motion.div>
                    
                    <h2 className="text-3xl font-bold mb-4 text-white">
                      {service.title}
                    </h2>
                    <div className="w-20 h-1 mb-6" style={{ background: `linear-gradient(90deg, ${service.color}, transparent)` }} />
                    <p className="text-lg text-gray-400 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                </div>

                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="rounded-3xl bg-gradient-to-br from-white/5 to-transparent border border-white/10 p-8">
                    <h3 className="text-xl font-semibold mb-6 text-white">Key Features</h3>
                    <ul className="space-y-4">
                      {service.features.map((feature, featureIndex) => (
                        <motion.li 
                          key={featureIndex} 
                          className="flex items-start gap-4 group"
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: featureIndex * 0.1 }}
                        >
                          <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: `${service.color}20` }}>
                            <CheckCircle2 className="w-4 h-4" style={{ color: service.color }} />
                          </div>
                          <span className="text-gray-400 group-hover:text-white transition-colors">{feature}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden" data-testid="services-cta-section">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] via-[#111] to-[#0a0a0a]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#3498DB] rounded-full filter blur-[200px] opacity-10" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#27AE60] rounded-full filter blur-[200px] opacity-10" />
        </div>
        
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-testid="services-cta-heading">
              Ready to <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#3498DB] to-[#27AE60]">Get Started</span>?
            </h2>
            <p className="text-xl text-gray-400 mb-10 leading-relaxed">
              Contact us today to discuss how we can help you achieve your goals in the UAE.
            </p>
            <Link 
              to="/contact" 
              className="inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-[#3498DB] to-[#27AE60] text-white font-bold text-lg rounded-full hover:shadow-[0_0_40px_rgba(52,152,219,0.5)] transition-all duration-300"
              data-testid="services-cta-button"
            >
              Schedule a Consultation
              <ArrowRight size={22} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;
